<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { apiGet, apiPost } from "@/utils/request";
import { API_ENDPOINTS } from "@/constants";
import { requireAuth } from "@/utils/auth";

/**
 * DNSSEC 管理页面组件
 */
export default {
  page() {
    return {
      title: this.$t("menuitems.dns.dnssec"),
      meta: [{ name: "description" }],
    };
  },
  components: { Layout, PageHeader },
  data() {
    return {
      title: "",
      items: [],
      loading: true,
      dnssec: {
        tag: null,
        algo: null,
        algotype: null,
        digest: {}
      },
      dnssecstatus: 503,
      digest: "",
      domain: "",
      availableDigestTypes: []
    };
  },
  mounted() {
    this.initPage();
    this.domain = this.$route.params.domain;
    this.getdnssec();
  },
  watch: {
    // 监听 digest 数据变化，自动选择第一个可用的 digest type
    'dnssec.digest': {
      handler(newVal) {
        console.log('DNSSEC digest changed:', newVal);
        if (newVal && typeof newVal === 'object' && Object.keys(newVal).length > 0) {
          // 过滤掉空值
          const validTypes = Object.keys(newVal).filter(key => newVal[key]);
          this.availableDigestTypes = validTypes;
          // 如果没有选中 digest 或选中的不存在，自动选择第一个
          if (!this.digest || !newVal[this.digest]) {
            this.digest = this.availableDigestTypes[0] || "";
          }
        } else {
          this.availableDigestTypes = [];
          this.digest = "";
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    /**
     * 初始化页面
     */
    initPage() {
      const domain = this.$route.params.domain;
      this.title = `${domain} - ${this.$t("menuitems.dns.dnssec")}`;
      this.items = [
        {
          text: this.$t("menuitems.dns.text"),
          href: "/",
        },
        {
          text: this.$t("menuitems.dns.list"),
          href: "/dns/list",
        },
        {
          text: domain,
          href: `/dns/${domain}/record`,
        },
        {
          text: this.$t("menuitems.dns.dnssec"),
          active: true,
        },
      ];
    },

    /**
     * 获取 DNSSEC 状态
     */
    async getdnssec() {
      if (!requireAuth(this)) {
        return;
      }

      try {
        const response = await apiGet(API_ENDPOINTS.DNSSEC, {
          params: {
            domain: this.domain,
          },
        }, {
          vm: this,
          showLoading: true,
          loadingText: '加载 DNSSEC 信息...',
          showError: false
        });

        if (response && response.status === 200) {
          this.dnssecstatus = response.status;
          // 确保 digest 是一个对象
          const dnssecData = response.dnssec || {};
          this.dnssec = {
            tag: dnssecData.tag || null,
            algo: dnssecData.algo || null,
            algotype: dnssecData.algotype || null,
            digest: (dnssecData.digest && typeof dnssecData.digest === 'object') ? dnssecData.digest : {}
          };
          console.log('DNSSEC data loaded:', this.dnssec);
        } else if (response && response.status === 503) {
          // DNSSEC 未启用，这是正常状态
          this.dnssecstatus = 503;
          const dnssecData = response.dnssec || {};
          this.dnssec = {
            tag: dnssecData.tag || null,
            algo: dnssecData.algo || null,
            algotype: dnssecData.algotype || null,
            digest: (dnssecData.digest && typeof dnssecData.digest === 'object') ? dnssecData.digest : {}
          };
        } else {
          this.$message({
            message: response.error || '加载 DNSSEC 信息失败',
            type: 'error',
          });
        }
      } catch (error) {
        console.error('加载 DNSSEC 信息失败:', error);
        if (error.response && error.response.status === 503) {
          // 503 状态码可能是 DNSSEC 未启用
          if (error.response.data && error.response.data.status === 503) {
            this.dnssecstatus = 503;
            const dnssecData = error.response.data.dnssec || {};
            this.dnssec = {
              tag: dnssecData.tag || null,
              algo: dnssecData.algo || null,
              algotype: dnssecData.algotype || null,
              digest: (dnssecData.digest && typeof dnssecData.digest === 'object') ? dnssecData.digest : {}
            };
          } else {
            this.$message({
              message: '加载 DNSSEC 信息失败',
              type: 'error',
            });
          }
        } else {
          this.$message({
            message: error.response?.data?.error || '加载 DNSSEC 信息失败',
            type: 'error',
          });
        }
      } finally {
        this.loading = false;
      }
    },

    /**
     * 启用 DNSSEC
     */
    async enablednssec() {
      if (!requireAuth(this)) {
        return;
      }

      this.$confirm('确定要启用 DNSSEC 吗？此操作可能需要几分钟时间。', '确认启用 DNSSEC', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await apiPost(API_ENDPOINTS.DNSSEC_ENABLE, {
            domain: this.domain,
          }, {}, {
            vm: this,
            showLoading: true,
            loadingText: '正在启用 DNSSEC...',
            showSuccess: true,
            successMessage: 'DNSSEC 启用成功',
            showError: true
          });

          if (response && response.status === 200) {
            // 重新获取 DNSSEC 状态
            await this.getdnssec();
          }
        } catch (error) {
          console.error('启用 DNSSEC 失败:', error);
          // 错误已在 apiPost 中处理
        }
      }).catch(() => {
        // 用户取消操作
      });
    },

    /**
     * 禁用 DNSSEC
     */
    async disablednssec() {
      if (!requireAuth(this)) {
        return;
      }

      this.$confirm('确定要禁用 DNSSEC 吗？禁用后域名将不再受到 DNSSEC 保护。', '确认禁用 DNSSEC', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await apiPost(API_ENDPOINTS.DNSSEC_DISABLE, {
            domain: this.domain,
          }, {}, {
            vm: this,
            showLoading: true,
            loadingText: '正在禁用 DNSSEC...',
            showSuccess: true,
            successMessage: 'DNSSEC 禁用成功',
            showError: true
          });

          if (response && response.status === 200) {
            // 重新获取 DNSSEC 状态
            await this.getdnssec();
          }
        } catch (error) {
          console.error('禁用 DNSSEC 失败:', error);
          // 错误已在 apiPost 中处理
        }
      }).catch(() => {
        // 用户取消操作
      });
    },

    /**
     * 复制文本到剪贴板
     */
    async copyToClipboard(text, label) {
      if (!text) {
        this.$message({
          message: '没有可复制的内容',
          type: 'warning',
        });
        return;
      }

      try {
        await navigator.clipboard.writeText(text);
        this.$message({
          message: `${label || '内容'}已复制到剪贴板`,
          type: 'success',
        });
      } catch (error) {
        // 降级方案：使用传统方法
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          this.$message({
            message: `${label || '内容'}已复制到剪贴板`,
            type: 'success',
          });
        } catch (err) {
          this.$message({
            message: '复制失败，请手动复制',
            type: 'error',
          });
        }
        document.body.removeChild(textArea);
      }
    },

    /**
     * 格式化算法显示
     */
    formatAlgorithm() {
      if (!this.dnssec.algo && !this.dnssec.algotype) {
        return '-';
      }
      // 优先显示 algotype（字符串格式），如果没有则显示 algo
      if (this.dnssec.algotype) {
        return this.dnssec.algotype;
      }
      return this.dnssec.algo || '-';
    },
    
    /**
     * 获取 Digest Type 标签
     */
    getDigestTypeLabel(type) {
      const labels = {
        'sha1': 'SHA1',
        'sha256': 'SHA256',
        'sha384': 'SHA384'
      };
      return labels[type] || type.toUpperCase();
    }
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="ri-shield-check-line me-2"></i>DNSSEC 管理
            </h5>
          </div>
          <div class="card-body" v-loading="loading">
            <!-- 操作按钮 -->
            <div class="mb-4">
              <el-button
                type="primary"
                :icon="dnssecstatus === 503 ? 'el-icon-check' : ''"
                v-if="dnssecstatus === 503"
                @click="enablednssec"
                :loading="loading"
              >
                {{ $t("dns.dnssec.enable") || "启用 DNSSEC" }}
              </el-button>
              <el-button
                type="danger"
                icon="el-icon-close"
                v-if="dnssecstatus === 200"
                @click="disablednssec"
                :loading="loading"
              >
                {{ $t("dns.dnssec.disable") || "禁用 DNSSEC" }}
              </el-button>
            </div>

            <!-- DNSSEC 状态提示 -->
            <el-alert
              v-if="dnssecstatus === 503"
              :title="$t('dns.dnssec.notenabled') || 'DNSSEC 未启用'"
              :description="$t('dns.dnssec.notenabled.desc') || '该域名尚未启用 DNSSEC。启用 DNSSEC 可以为您的域名提供额外的安全保障。'"
              type="info"
              :closable="false"
              show-icon
              class="mb-4"
            />

            <el-alert
              v-if="dnssecstatus === 200"
              title="DNSSEC 已启用"
              description="您的域名已启用 DNSSEC，域名解析受到 DNSSEC 保护。"
              type="success"
              :closable="false"
              show-icon
              class="mb-4"
            />

            <!-- DNSSEC 信息表格 -->
            <div v-if="dnssecstatus === 200" class="table-responsive">
              <table class="table table-bordered table-hover">
                <tbody>
                  <tr>
                    <td style="width: 200px; font-weight: 600;">
                      <i class="ri-hashtag me-2"></i>{{ $t("dns.dnssec.tag") || "Tag" }} (tag)
                    </td>
                    <td>
                      <div class="d-flex align-items-center justify-content-between">
                        <span class="font-monospace">{{ dnssec.tag || '-' }}</span>
                        <el-button
                          v-if="dnssec.tag"
                          type="text"
                          size="small"
                          icon="el-icon-document-copy"
                          @click="copyToClipboard(dnssec.tag, 'Tag')"
                        >
                          复制
                        </el-button>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td style="font-weight: 600;">
                      <i class="ri-settings-3-line me-2"></i>{{ $t("dns.dnssec.algorithm") || "Algorithm" }} (algo)
                    </td>
                    <td>
                      <div class="d-flex align-items-center justify-content-between">
                        <span class="font-monospace">{{ formatAlgorithm() }}</span>
                        <el-button
                          v-if="formatAlgorithm() !== '-'"
                          type="text"
                          size="small"
                          icon="el-icon-document-copy"
                          @click="copyToClipboard(formatAlgorithm(), 'Algorithm')"
                        >
                          复制
                        </el-button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="dnssecstatus === 200">
                    <td style="font-weight: 600;">
                      <i class="ri-file-list-3-line me-2"></i>{{ $t("dns.dnssec.digest") || "Digest Type" }} (digest type)
                    </td>
                    <td>
                      <el-select 
                        v-model="digest" 
                        placeholder="请选择 Digest Type"
                        style="width: 100%; max-width: 300px;"
                        :disabled="availableDigestTypes.length === 0"
                        v-if="availableDigestTypes.length > 0"
                      >
                        <el-option
                          v-for="type in availableDigestTypes"
                          :key="type"
                          :label="getDigestTypeLabel(type)"
                          :value="type"
                        />
                      </el-select>
                      <span v-else class="text-muted">-</span>
                      <small v-if="availableDigestTypes.length === 0" class="text-muted d-block mt-1">
                        暂无可用的 Digest Type（DS 记录可能尚未生成）
                      </small>
                    </td>
                  </tr>
                  <tr v-if="dnssecstatus === 200">
                    <td style="font-weight: 600;">
                      <i class="ri-fingerprint-line me-2"></i>{{ $t("dns.dnssec.digestvalue") || "Digest Value" }} (digest)
                    </td>
                    <td>
                      <div v-if="digest && dnssec.digest && dnssec.digest[digest]" class="d-flex align-items-center justify-content-between flex-wrap">
                        <span class="font-monospace text-break me-2" style="word-break: break-all; flex: 1; min-width: 0;">
                          {{ dnssec.digest[digest] }}
                        </span>
                        <el-button
                          type="text"
                          size="small"
                          icon="el-icon-document-copy"
                          @click="copyToClipboard(dnssec.digest[digest], 'Digest')"
                          class="flex-shrink-0"
                        >
                          复制
                        </el-button>
                      </div>
                      <div v-else-if="!digest && availableDigestTypes.length > 0" class="text-muted">
                        请先选择 Digest Type
                      </div>
                      <span v-else class="text-muted">-</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- 空状态提示 -->
            <div v-if="dnssecstatus === 503" class="text-center py-5">
              <i class="ri-shield-line" style="font-size: 64px; color: #ccc;"></i>
              <p class="text-muted mt-3">该域名尚未启用 DNSSEC</p>
            </div>
          </div>
        </div>

        <!-- DS 记录说明 -->
        <div v-if="dnssecstatus === 200 && dnssec.digest && Object.keys(dnssec.digest).length > 0" class="card mt-4">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="ri-information-line me-2"></i>DS 记录说明
            </h5>
          </div>
          <div class="card-body">
            <p class="mb-3">请在您的域名注册商处添加以下 DS 记录：</p>
            <div v-for="(digestValue, digestType) in dnssec.digest" :key="digestType" class="mb-3">
              <div class="alert alert-info mb-2">
                <strong>Digest Type:</strong> {{ digestType.toUpperCase() }}
              </div>
              <div class="font-monospace p-2 bg-light rounded">
                {{ dnssec.tag }} {{ dnssec.algo }} {{ digestType === 'sha1' ? '1' : digestType === 'sha256' ? '2' : '4' }} {{ digestValue }}
              </div>
              <el-button
                type="text"
                size="small"
                icon="el-icon-document-copy"
                @click="copyToClipboard(`${dnssec.tag} ${dnssec.algo} ${digestType === 'sha1' ? '1' : digestType === 'sha256' ? '2' : '4'} ${digestValue}`, 'DS 记录')"
                class="mt-2"
              >
                复制 DS 记录
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.font-monospace {
  font-family: 'Courier New', Courier, monospace;
  font-size: 14px;
}

.text-break {
  word-break: break-all;
  word-wrap: break-word;
}

.table td {
  vertical-align: middle;
}

.me-2 {
  margin-right: 0.5rem;
}

.mb-4 {
  margin-bottom: 1.5rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 1rem;
}

.py-5 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.card-title {
  font-size: 1.1rem;
  font-weight: 600;
}

.alert {
  padding: 0.75rem 1rem;
  border-radius: 0.25rem;
}

.alert-info {
  background-color: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

.bg-light {
  background-color: #f8f9fa;
}

.rounded {
  border-radius: 0.25rem;
}

.p-2 {
  padding: 0.5rem;
}
</style>
